import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts.vfs;

const COLORS = {
  teal: "#00758f",
  orange: "#f4a51c",
  text: "#445b63",
  light: "#dce7e9",
};

const value = input => (input == null ? "" : String(input).trim());
const fullName = person => [value(person && person.name), value(person && person.lastname)].filter(Boolean).join(" ");

function contactLines(entity, includeInternal = false) {
  return [
    value(entity && entity.address) && `Dirección: ${value(entity.address)}`,
    value(entity && entity.phone) && `Teléfono: ${value(entity.phone)}`,
    includeInternal && value(entity && entity.internal_phone) && `Interno: ${value(entity.internal_phone)}`,
    value(entity && entity.whatsapp) && `WhatsApp: ${value(entity.whatsapp)}`,
    value(entity && entity.celphone) && `Celular: ${value(entity.celphone)}`,
    value(entity && entity.email) && `Email: ${value(entity.email)}`,
  ].filter(Boolean).join("\n");
}

function personColumns(person) {
  return {
    columns: [
      {
        width: "42%",
        stack: [
          { text: fullName(person), style: "personName" },
          { text: value(person && person.responsability), style: "personData" },
        ],
      },
      { width: "*", text: contactLines(person, true), style: "personData" },
    ],
    columnGap: 12,
    margin: [0, 4, 0, 4],
  };
}

function peopleFromDepartment(department) {
  if (!department) return [];
  if (Array.isArray(department)) return department.filter(Boolean);
  if (Array.isArray(department.boss)) return department.boss.filter(Boolean);
  return department.boss ? [department.boss] : [];
}

function addDivider(content) {
  content.push({
    canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, lineColor: COLORS.orange }],
    margin: [0, 6, 0, 8],
  });
}

function addCasaCentral(content, casaCentral) {
  const delegation = casaCentral.delegation || {};
  content.push({ text: value(delegation.name) || "OSFATUN Casa Central", style: "province", margin: [0, 18, 0, 4] });
  content.push({
    columns: [
      { width: "42%", text: "Información institucional", style: "unitName" },
      {
        width: "*",
        text: [
          value(delegation.address) && `Dirección: ${value(delegation.address)}`,
          value(delegation.city) && `Localidad: ${value(delegation.city)}`,
          value(delegation.province) && `Provincia: ${value(delegation.province)}`,
          value(delegation.phone) && `Teléfono: ${value(delegation.phone)}`,
          value(delegation.whatsapp) && `WhatsApp: ${value(delegation.whatsapp)}`,
          value(delegation.email) && `Email: ${value(delegation.email)}`,
        ].filter(Boolean).join("\n"),
        style: "unitData",
      },
    ],
    margin: [0, 5, 0, 8],
  });

  const gerencies = (casaCentral.gerencies || []).filter(item => item && item.manager);
  if (gerencies.length) {
    content.push({ text: "GERENCIAS DE OSFATUN", style: "section" });
    gerencies.forEach(item => {
      content.push({ text: value(item.name), style: "subsection" });
      content.push(personColumns(item.manager));
    });
  }

  const bosses = [
    ["BENEFICIARIOS", casaCentral.beneficiarios],
    ["FISCALIZACIÓN", casaCentral.fiscalizacion],
    ["PRESTACIONES", casaCentral.prestaciones],
  ].map(([name, source]) => [name, peopleFromDepartment(source)]).filter(([, people]) => people.length);

  if (bosses.length) {
    content.push({ text: "JEFATURAS DE OSFATUN", style: "section" });
    bosses.forEach(([name, people]) => {
      content.push({ text: name, style: "subsection" });
      people.forEach(person => content.push(personColumns(person)));
    });
  }

  const departments = (casaCentral.departamentos || []).filter(item => item && Array.isArray(item.users) && item.users.length);
  if (departments.length) {
    content.push({ text: "INTEGRANTES DE LOS DEPARTAMENTOS", style: "section", pageBreak: "before" });
    departments.forEach(department => {
      content.push({ text: value(department.name), style: "subsection", margin: [0, 10, 0, 3] });
      department.users.filter(user => user && user.name).forEach(user => content.push(personColumns(user)));
      addDivider(content);
    });
  }
}

export function buildDatosUnidadesDocument(delegations, casaCentral, now = new Date()) {
  const content = [];
  const month = now.toLocaleString("es-AR", { month: "long" }).toLocaleUpperCase("es-AR");
  content.push({ text: `${month} - ${now.getFullYear()}`, alignment: "right", style: "date", margin: [0, 0, 0, 18] });

  let previousProvince = null;
  let previousCity = null;
  (delegations || []).forEach(delegation => {
    if (delegation.province !== previousProvince) {
      content.push({ text: value(delegation.province), style: "province" });
      previousProvince = delegation.province;
      previousCity = null;
    }
    if (delegation.city !== previousCity) {
      content.push({ text: value(delegation.city), style: "city" });
      previousCity = delegation.city;
    }
    content.push({
      columns: [
        { width: "42%", text: value(delegation.name), style: "unitName" },
        { width: "*", text: contactLines(delegation), style: "unitData" },
      ],
      columnGap: 12,
      margin: [0, 7, 0, 6],
    });
    const users = Array.isArray(delegation.user) ? delegation.user.filter(user => user && user.name) : [];
    if (users.length) {
      content.push({ text: "Integrantes de la delegación", style: "members" });
      users.forEach(user => content.push(personColumns(user)));
    }
    addDivider(content);
  });

  addCasaCentral(content, casaCentral || {});

  return {
    pageSize: "A4",
    pageMargins: [40, 62, 40, 48],
    header(currentPage, pageCount) {
      return {
        table: {
          widths: ["*", "*"],
          body: [[
            { text: "OSFATUN", color: "#ffffff", bold: true, fontSize: 15, fillColor: COLORS.teal, margin: [28, 10, 0, 10] },
            { text: "DATOS Y UNIDADES", color: "#ffffff", bold: true, alignment: "right", fontSize: 10, fillColor: COLORS.teal, margin: [0, 13, 28, 10] },
          ]],
        },
        layout: "noBorders",
      };
    },
    footer(currentPage, pageCount) {
      return {
        columns: [
          { text: "www.osfatun.com.ar", color: COLORS.teal, fontSize: 8 },
          { text: `${currentPage} / ${pageCount}`, color: COLORS.text, alignment: "right", fontSize: 8 },
        ],
        margin: [40, 16, 40, 0],
      };
    },
    content,
    defaultStyle: { font: "Roboto", color: COLORS.text },
    styles: {
      date: { fontSize: 13, bold: true, color: COLORS.teal },
      province: { fontSize: 21, bold: true, color: COLORS.teal, margin: [0, 10, 0, 2] },
      city: { fontSize: 16, color: COLORS.teal, margin: [0, 8, 0, 5] },
      unitName: { fontSize: 10, bold: true },
      unitData: { fontSize: 8.5, lineHeight: 1.2 },
      members: { fontSize: 11, color: COLORS.teal, margin: [0, 7, 0, 4] },
      personName: { fontSize: 9.5, bold: true },
      personData: { fontSize: 8.2, lineHeight: 1.15 },
      section: { fontSize: 15, bold: true, color: COLORS.teal, margin: [0, 16, 0, 8] },
      subsection: { fontSize: 11, bold: true, color: COLORS.orange, margin: [0, 6, 0, 2] },
    },
  };
}

export function downloadDatosUnidadesPdf(delegations, casaCentral, now = new Date()) {
  const month = now.toLocaleString("es-AR", { month: "long" }).toLocaleUpperCase("es-AR");
  const filename = `Datos_y_Unidades_${month}-${now.getFullYear()}.pdf`;
  pdfMake.createPdf(buildDatosUnidadesDocument(delegations, casaCentral, now)).download(filename);
}
